import { useEffect, useRef } from 'react';

const INGESTRO_VERIFY_URL = 'https://api-gateway.ingestro.com/sdk/v1/verify';
function isVerifyUrl(url: string): boolean {
  return url.includes(INGESTRO_VERIFY_URL) || url.includes('/sdk/v1/verify');
}

export type UseImportEmployeesProps = {
  ingestroError: Error | null;
  setIngestroError: (error: Error | null) => void;
};

const getUrl = (input: RequestInfo | URL): string => {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
};

export const useImportEmployees = ({
  ingestroError,
  setIngestroError
}: UseImportEmployeesProps) => {
  const setErrorRef = useRef((err: Error | null) => {
    setIngestroError(err);
  });
  setErrorRef.current = (err: Error | null) => {
    setIngestroError(err);
  };

  useEffect(() => {
    if (ingestroError) return;

    const originalFetch = window.fetch;
    window.fetch = async (
      input: RequestInfo | URL,
      init?: RequestInit
    ): Promise<Response> => {
      if (!isVerifyUrl(getUrl(input))) {
        return originalFetch(input, init);
      }
      try {
        const response = await originalFetch(input, init);
        if (!response.ok) {
          const clone = response.clone();
          let body: string | undefined;
          try {
            body = await clone.text();
          } catch {
            body = undefined;
          }
          setErrorRef.current(
            new Error(
              `Import service error: ${response.status}${
                body ? ` - ${body}` : ''
              }`
            )
          );
        }
        return response;
      } catch (err) {
        setErrorRef.current(
          err instanceof Error ? err : new Error(String(err))
        );
        throw err;
      }
    };

    const OriginalXHR = window.XMLHttpRequest;
    window.XMLHttpRequest = function () {
      const xhr = new OriginalXHR();
      const originalOpen = OriginalXHR.prototype.open;
      xhr.open = function (
        method: string,
        url: string,
        async?: boolean,
        username?: string | null,
        password?: string | null
      ) {
        xhr.addEventListener('load', function () {
          if (isVerifyUrl(url) && xhr.status >= 400) {
            setErrorRef.current(
              new Error(
                `Import service error: ${xhr.status} ${xhr.statusText || ''}`
              )
            );
          }
        });
        xhr.addEventListener('error', function () {
          if (isVerifyUrl(url)) {
            setErrorRef.current(
              new Error(
                'Import service is unavailable. Please check your connection.'
              )
            );
          }
        });
        return originalOpen.call(
          this,
          method,
          url,
          async ?? true,
          username ?? null,
          password ?? null
        );
      };
      return xhr;
    } as unknown as typeof XMLHttpRequest;

    return () => {
      window.fetch = originalFetch;
      window.XMLHttpRequest = OriginalXHR;
    };
  }, [ingestroError]);
};
